import React, { useCallback, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule, themeBalham } from "ag-grid-community";
import { Box, Button, Grid } from "@mui/material";
import { FileDownload, Search as SearchIcon } from "@mui/icons-material";
import dayjs from "dayjs";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";
import { getProductList } from "../../api/products";
import { getSyntheticInventoryReport } from "../../api/stockmoves/index.ts";
import { useCompany } from "../../contexts/CompanyContext";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import { useAgGridPersistedPagination } from "../../hooks/usePersistedPagination";

ModuleRegistry.registerModules([AllCommunityModule]);

const escapeCsvCell = (value) => {
  if (value == null || value === "") return '""';
  const stringValue = String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
};

const formatNumber = (value, decimals = 2) => {
  if (value == null || value === "") return "";
  return Number(value).toLocaleString("es-MX", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export default function SyntheticInventoryReportTable() {
  const { selectedCompany } = useCompany();
  const { openSnack } = useSnack();
  const { paginationPageSize, onPaginationChanged, restorePage } =
    useAgGridPersistedPagination();

  const [startDate, setStartDate] = useState(() => dayjs().subtract(1, "week"));
  const [endDate, setEndDate] = useState(() => dayjs());
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!selectedCompany?.id || !startDate || !endDate) return;

    setLoading(true);
    try {
      const data = await getSyntheticInventoryReport({
        companyId: selectedCompany.id,
        startDate: startDate.format("YYYY-MM-DD"),
        endDate: endDate.format("YYYY-MM-DD"),
        ...(selectedProduct?.id ? { productId: selectedProduct.id } : {}),
      });
      setRowData(Array.isArray(data) ? data : []);
    } catch (error) {
      openSnack(handleError(error), "error");
      setRowData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany?.id, startDate, endDate, selectedProduct, openSnack]);

  const handleExportCsv = () => {
    if (rowData.length === 0) {
      openSnack("No hay datos para exportar", "warning");
      return;
    }

    const headers = [
      "Código",
      "Existencia inicial",
      "Entradas",
      "Salidas",
      "Existencia final",
      "Precio unitario",
      "Valor",
    ];

    const csvRows = rowData.map((row) =>
      [
        escapeCsvCell(row.productCode),
        escapeCsvCell(row.initialStock),
        escapeCsvCell(row.newInbounds),
        escapeCsvCell(row.newOutbounds),
        escapeCsvCell(row.newStock),
        escapeCsvCell(row.newUnitPrice ?? ""),
        escapeCsvCell(row.value ?? ""),
      ].join(",")
    );

    const csvContent = `\uFEFF${headers.map(escapeCsvCell).join(",")}\n${csvRows.join("\n")}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `movimientos_sinteticos_${dayjs().format("YYYYMMDD_HHmmss")}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const columnDefs = useMemo(
    () => [
      {
        field: "productCode",
        headerName: "Código",
        minWidth: 120,
      },
      {
        field: "initialStock",
        headerName: "Existencia inicial",
        type: "numericColumn",
        valueFormatter: (params) => formatNumber(params.value, 2),
      },
      {
        field: "newInbounds",
        headerName: "Entradas",
        type: "numericColumn",
        valueFormatter: (params) => formatNumber(params.value, 2),
      },
      {
        field: "newOutbounds",
        headerName: "Salidas",
        type: "numericColumn",
        valueFormatter: (params) => formatNumber(params.value, 2),
      },
      {
        field: "newStock",
        headerName: "Existencia final",
        type: "numericColumn",
        valueFormatter: (params) => formatNumber(params.value, 2),
      },
      {
        field: "newUnitPrice",
        headerName: "Precio unitario",
        type: "numericColumn",
        valueFormatter: (params) => formatNumber(params.value, 4),
      },
      {
        field: "value",
        headerName: "Valor",
        type: "numericColumn",
        valueFormatter: (params) => formatNumber(params.value, 2),
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      minWidth: 90,
    }),
    []
  );

  return (
    <Box width="100%">
      <Grid container direction="row" spacing={2} mb={2} alignItems="center">
        <Grid item>
          <MyDatePicker
            label="Fecha inicio"
            value={startDate}
            onChange={(value) => setStartDate(value)}
          />
        </Grid>
        <Grid item>
          <MyDatePicker
            label="Fecha fin"
            value={endDate}
            onChange={(value) => setEndDate(value)}
          />
        </Grid>
        <Grid item sx={{ minWidth: 220 }}>
          <LGAutocomplete
            label="Producto"
            fetchFunction={getProductList}
            queryParams={{ companyId: selectedCompany?.id, pageSize: 20 }}
            optionLabel="code"
            value={selectedProduct}
            onChange={(_, value) => setSelectedProduct(value)}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
          />
        </Grid>
        <Grid item>
          <Button variant="contained" startIcon={<SearchIcon />} onClick={fetchData}>
            Buscar
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            startIcon={<FileDownload />}
            onClick={handleExportCsv}
            disabled={rowData.length === 0}
          >
            Descargar CSV
          </Button>
        </Grid>
      </Grid>

      <div style={{ height: 600, width: "100%" }}>
        <AgGridReact
          theme={themeBalham}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination
          paginationPageSize={paginationPageSize}
          paginationPageSizeSelector={[10, 20, 50, 100]}
          onPaginationChanged={onPaginationChanged}
          onFirstDataRendered={(params) => restorePage(params.api)}
          animateRows
          loading={loading}
          ensureDomOrder
          suppressColumnVirtualisation
          wrapHeaderText
          autoHeaderHeight
        />
      </div>
    </Box>
  );
}
